import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import { api } from '../services/api';

interface StateRow {
  state: string;
  stateCode: string;
  rate: number;
  permitNumber: string | null;
  filingFrequency: string;
  currentPeriod: string;
  periodStart: string;
  periodEnd: string;
  currentCollected: number;
  currentTaxable: number;
  currentOrderCount: number;
  dueDate: string;
  daysUntilDue: number;
  status: 'overdue' | 'due_soon' | 'current' | 'no_collection';
  lastFiledAt: string | null;
  lastFiling: any;
}

const STATUS_BADGE: Record<string, string> = {
  overdue: 'bg-red-100 text-red-800 border-red-300 font-bold',
  due_soon: 'bg-amber-100 text-amber-800 border-amber-300',
  current: 'bg-green-100 text-green-800 border-green-300',
  no_collection: 'bg-gray-100 text-gray-600 border-gray-200',
};
const STATUS_LABEL: Record<string, string> = {
  overdue: 'OVERDUE',
  due_soon: 'Due soon',
  current: 'Current',
  no_collection: 'No collection',
};

export default function TaxFilingsPage() {
  const [data, setData] = useState<{ states: StateRow[]; summary: any } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filing, setFiling] = useState<StateRow | null>(null);
  const [filingForm, setFilingForm] = useState({
    tax_remitted: '',
    confirmation_number: '',
    filed_at: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await api.tax.filingsDashboard();
      setData(d);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startFiling = (st: StateRow) => {
    setFiling(st);
    setFilingForm({
      tax_remitted: st.currentCollected.toFixed(2),
      confirmation_number: '',
      filed_at: new Date().toISOString().slice(0, 10),
      notes: '',
    });
  };

  const submitFiling = async () => {
    if (!filing) return;
    setSubmitting(true);
    try {
      await api.tax.recordFiling(filing.stateCode, {
        period_label: filing.currentPeriod,
        period_start: filing.periodStart,
        period_end: filing.periodEnd,
        tax_collected: filing.currentCollected,
        tax_remitted: parseFloat(filingForm.tax_remitted),
        taxable_amount: filing.currentTaxable,
        order_count: filing.currentOrderCount,
        filed_at: filingForm.filed_at,
        confirmation_number: filingForm.confirmation_number || undefined,
        notes: filingForm.notes || undefined,
      });
      setFiling(null);
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const [exporting, setExporting] = useState<string | null>(null);
  const exportWorksheet = async (st: StateRow) => {
    // period label is "YYYY-Qn"
    const m = /(\d{4})-Q([1-4])/.exec(st.currentPeriod);
    if (!m) { alert(`Worksheet export only supports quarterly periods (got ${st.currentPeriod}).`); return; }
    const year = parseInt(m[1]); const quarter = parseInt(m[2]);
    setExporting(st.stateCode);
    try {
      const w = await api.tax.returnWorksheet(st.stateCode, year, quarter);
      const esc = (s: any) => `"${String(s == null ? '' : s).replace(/"/g, '""')}"`;
      const money = (n: any) => (Math.round((parseFloat(n) || 0) * 100) / 100).toFixed(2);
      const L: string[] = [];
      L.push('Keder Screens — Sales Tax Return Worksheet');
      L.push([esc('State'), esc(`${w.stateCode} (${w.state})`)].join(','));
      L.push([esc('Permit #'), esc(w.permitNumber || '')].join(','));
      L.push([esc('Period'), esc(`${w.periodLabel} (${w.periodStart} → ${w.periodEnd})`)].join(','));
      L.push([esc('Rate'), esc(`${w.ratePercent}%`)].join(','));
      L.push('');
      L.push('Return Line Items,Amount');
      L.push([esc('Total sales (gross, incl. exempt)'), esc(money(w.lines.totalSales))].join(','));
      L.push([esc('  Less: exempt / resale sales'), esc(money(w.lines.exemptResaleSales))].join(','));
      L.push([esc('Taxable sales'), esc(money(w.lines.taxableSales))].join(','));
      L.push([esc('Taxable purchases (enter manually)'), esc(money(w.lines.taxablePurchases))].join(','));
      L.push([esc('Tax due (rate × taxable)'), esc(money(w.lines.taxDue))].join(','));
      L.push([esc('Tax collected'), esc(money(w.lines.taxCollected))].join(','));
      L.push('');
      L.push('Accrual memo (not in totals above),');
      L.push([esc('Unpaid taxable orders'), esc(w.accrualMemo.unpaidTaxableOrders)].join(','));
      L.push([esc('Tax not yet collected'), esc(money(w.accrualMemo.unpaidTaxNotYetCollected))].join(','));
      L.push('');
      L.push('Order detail (taxable orders)');
      L.push(['Order', 'Date', 'Dealer', 'City', 'ZIP', 'Taxable', 'Tax'].map(esc).join(','));
      (w.orders || []).forEach((o: any) => L.push(
        [o.order_number, o.order_date, o.company_name, o.shipping_city, o.shipping_zip, money(o.taxable), money(o.tax)].map(esc).join(','),
      ));
      L.push('');
      L.push('Notes');
      (w.notes || []).forEach((n: string) => L.push(esc(`- ${n}`)));

      const blob = new Blob([L.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tax-return-worksheet-${w.stateCode}-${w.periodLabel}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.message || 'Failed to export worksheet');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tax Filings"
        description="Per-state filing schedule, what's due, and how much. Use this view to drive your quarterly/monthly state filings."
      />

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">{error}</div>}

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="text-center">
            <p className="text-2xl font-bold text-gray-900">{data.summary.activeStates}</p>
            <p className="text-xs text-gray-500 uppercase">Active states</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-blue-700">{fmt(data.summary.totalCollectedThisPeriod)}</p>
            <p className="text-xs text-gray-500 uppercase">Collected this period</p>
          </Card>
          <Card className={`text-center ${data.summary.overdueCount > 0 ? 'border-l-4 border-l-red-500' : ''}`}>
            <p className={`text-2xl font-bold ${data.summary.overdueCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>{data.summary.overdueCount}</p>
            <p className="text-xs text-gray-500 uppercase">Overdue</p>
          </Card>
          <Card className={`text-center ${data.summary.dueSoonCount > 0 ? 'border-l-4 border-l-amber-500' : ''}`}>
            <p className={`text-2xl font-bold ${data.summary.dueSoonCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>{data.summary.dueSoonCount}</p>
            <p className="text-xs text-gray-500 uppercase">Due in ≤14 days</p>
          </Card>
        </div>
      )}

      <Card title="States actively collecting">
        {loading ? <LoadingSpinner /> : !data || data.states.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No states actively collecting. Enable a state in
            <Link to="/settings/sales-tax-nexus" className="text-blue-600 underline mx-1">Settings → Sales Tax & Nexus</Link>
            first.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b">
                  <th className="px-2 py-2">State</th>
                  <th className="px-2 py-2">Permit #</th>
                  <th className="px-2 py-2">Period</th>
                  <th className="px-2 py-2 text-right">Collected</th>
                  <th className="px-2 py-2 text-right">Orders</th>
                  <th className="px-2 py-2">Due date</th>
                  <th className="px-2 py-2 text-center">Status</th>
                  <th className="px-2 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.states.map(st => (
                  <tr key={st.stateCode} className={`border-b border-gray-100 ${
                    st.status === 'overdue' ? 'bg-red-50' :
                    st.status === 'due_soon' ? 'bg-amber-50' : ''
                  }`}>
                    <td className="px-2 py-2 font-mono font-semibold">
                      {st.stateCode}
                      <div className="text-xs text-gray-500 font-normal">{st.state} · {(st.rate * 100).toFixed(2)}% · {st.filingFrequency}</div>
                    </td>
                    <td className="px-2 py-2 font-mono text-xs">{st.permitNumber || <span className="text-gray-400">—</span>}</td>
                    <td className="px-2 py-2">
                      <div className="font-medium">{st.currentPeriod}</div>
                      <div className="text-xs text-gray-500">{st.periodStart} → {st.periodEnd}</div>
                    </td>
                    <td className="px-2 py-2 text-right font-mono font-semibold">
                      {fmt(st.currentCollected)}
                      {st.currentTaxable > 0 && (
                        <div className="text-xs text-gray-400 font-normal">on {fmt(st.currentTaxable)}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">{st.currentOrderCount}</td>
                    <td className="px-2 py-2">
                      <div className="font-mono">{st.dueDate}</div>
                      <div className={`text-xs ${st.daysUntilDue < 0 ? 'text-red-700 font-bold' : st.daysUntilDue <= 14 ? 'text-amber-700' : 'text-gray-500'}`}>
                        {st.daysUntilDue < 0 ? `${Math.abs(st.daysUntilDue)} days late` : st.daysUntilDue === 0 ? 'today' : `in ${st.daysUntilDue} days`}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`px-2 py-0.5 text-xs rounded border ${STATUS_BADGE[st.status]}`}>
                        {STATUS_LABEL[st.status]}
                      </span>
                      {st.lastFiledAt && (
                        <div className="text-[10px] text-gray-400 mt-1">last filed {st.lastFiledAt.slice(0, 10)}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap">
                      <button
                        onClick={() => exportWorksheet(st)}
                        disabled={exporting === st.stateCode}
                        title="Download return worksheet (Webfile line items) for this period"
                        className="px-2 py-1 mr-1 bg-gray-100 text-gray-700 border border-gray-300 text-xs rounded hover:bg-gray-200 disabled:opacity-40"
                      >
                        {exporting === st.stateCode ? '…' : 'Worksheet'}
                      </button>
                      <button
                        onClick={() => startFiling(st)}
                        disabled={st.currentCollected === 0}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-30"
                      >
                        Mark filed
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Need historical totals? See
          <Link to="/reports/tax-collected" className="text-blue-600 underline mx-1">Reports → Tax Collected</Link>
          for the state × quarter pivot.
        </p>
      </Card>

      {filing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h3 className="text-lg font-semibold mb-1">Mark filing for {filing.state} ({filing.stateCode})</h3>
            <p className="text-xs text-gray-500 mb-4">
              Period <strong>{filing.currentPeriod}</strong> ({filing.periodStart} → {filing.periodEnd}) · Collected <strong>{fmt(filing.currentCollected)}</strong> on <strong>{fmt(filing.currentTaxable)}</strong> across <strong>{filing.currentOrderCount}</strong> orders
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Filed on</label>
                <input
                  type="date"
                  value={filingForm.filed_at}
                  onChange={(e) => setFilingForm(f => ({ ...f, filed_at: e.target.value }))}
                  className="w-full px-2 py-1 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Tax remitted ($)</label>
                <input
                  type="number" step="0.01"
                  value={filingForm.tax_remitted}
                  onChange={(e) => setFilingForm(f => ({ ...f, tax_remitted: e.target.value }))}
                  className="w-full px-2 py-1 border rounded font-mono"
                />
                <p className="text-xs text-gray-400 mt-0.5">Defaults to collected amount. Adjust if state portal computed differently (rounding, prepayment credits, etc.)</p>
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Confirmation #</label>
                <input
                  value={filingForm.confirmation_number}
                  onChange={(e) => setFilingForm(f => ({ ...f, confirmation_number: e.target.value }))}
                  className="w-full px-2 py-1 border rounded font-mono"
                  placeholder="WebFile confirmation, transaction ID, etc."
                />
              </div>
              <div>
                <label className="block text-xs uppercase text-gray-500 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={filingForm.notes}
                  onChange={(e) => setFilingForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setFiling(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
              <button
                onClick={submitFiling}
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Recording...' : 'Record filing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
